/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { GeometryDto } from 'customer-speed-api';
import { ILatLng } from '../../core/models';

/**
 * Decodes a single delta-encoded geometry link from the HERE segment API.
 * Each entry has comma-separated values where the first value is the
 * base coordinate (in 1e-5 degrees) and subsequent values are deltas.
 * e.g. "4378444,21,19" → [43.78444, 43.78465, 43.78484]
 *
 * When direction is "-", the coordinates are in reverse topological order.
 * We reverse them so all links flow in the same A→B direction before stitching.
 */
function decodeLink(entry: GeometryDto): ILatLng[] {
  const latParts = entry.lat.split(',').map(v => (v === '' ? 0 : Number(v)));
  const lngParts = entry.lng.split(',').map(v => (v === '' ? 0 : Number(v)));

  const points: ILatLng[] = [];
  let lat = latParts[0];
  let lng = lngParts[0];
  points.push({ lat: lat / 1e5, lng: lng / 1e5 });

  const maxDeltas = Math.max(latParts.length, lngParts.length);
  for (let i = 1; i < maxDeltas; i++) {
    lat += latParts[i] || 0;
    lng += lngParts[i] || 0;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  if (entry.direction === GeometryDto.DirectionEnum.Minus) {
    points.reverse();
  }

  return points;
}

/**
 * Calculates squared distance between two points (avoids sqrt for performance).
 */
function distanceSq(a: ILatLng, b: ILatLng): number {
  const dLat = a.lat - b.lat;
  const dLng = a.lng - b.lng;
  return dLat * dLat + dLng * dLng;
}

interface IDecodedLink {
  points: ILatLng[];
  start: ILatLng;
  end: ILatLng;
}

/**
 * Finds the nearest unused link to the current endpoint.
 * Returns the index and whether it needs to be reversed.
 */
function findNearestLink(currentEnd: ILatLng, links: IDecodedLink[], used: Set<number>): { index: number; reversed: boolean } | null {
  let bestIndex = -1;
  let bestDist = Infinity;
  let bestReversed = false;

  for (let i = 0; i < links.length; i++) {
    if (used.has(i)) continue;

    const distToStart = distanceSq(currentEnd, links[i].start);
    const distToEnd = distanceSq(currentEnd, links[i].end);

    if (distToStart < bestDist) {
      bestDist = distToStart;
      bestIndex = i;
      bestReversed = false;
    }
    if (distToEnd < bestDist) {
      bestDist = distToEnd;
      bestIndex = i;
      bestReversed = true;
    }
  }

  return bestIndex === -1 ? null : { index: bestIndex, reversed: bestReversed };
}

/**
 * Decodes and reorders geometry links from the HERE segment API into a
 * single contiguous path using a nearest-neighbor algorithm.
 *
 * The geometry array contains links that may be unordered and some may have
 * their coordinates reversed. This function picks the best next link by
 * finding the one whose start or end is closest to the current path's
 * endpoint, reversing the link if needed to maintain continuity.
 *
 * @param geometry - Array of delta-encoded geometry entries from the API
 * @returns Decoded array of lat/lng coordinate pairs in correct spatial order
 */
export function decodeGeometry(geometry: GeometryDto[]): ILatLng[] {
  if (!geometry?.length) return [];

  const links: IDecodedLink[] = geometry.map(entry => {
    const points = decodeLink(entry);
    return { points, start: points[0], end: points[points.length - 1] };
  });

  if (links.length === 1) return links[0].points;

  // Stitch links using nearest-neighbor starting from index 0
  const used = new Set<number>([0]);
  const result: ILatLng[] = [...links[0].points];
  let currentEnd = links[0].end;

  while (used.size < links.length) {
    const nearest = findNearestLink(currentEnd, links, used);
    if (!nearest) break;

    used.add(nearest.index);
    const nextPoints = nearest.reversed ? [...links[nearest.index].points].reverse() : links[nearest.index].points;

    result.push(...nextPoints.slice(1));
    currentEnd = nextPoints[nextPoints.length - 1];
  }

  // Normalize orientation: ensure the polyline flows in the forward (A→B)
  // topological direction. Find the first "+" link and check if its start
  // point is closer to result[0] or result[last]. If closer to result[last],
  // the polyline is backwards — reverse it.
  const firstPlusIndex = geometry.findIndex(g => g.direction === GeometryDto.DirectionEnum.Plus);
  if (firstPlusIndex >= 0 && result.length >= 2) {
    const refPoint = links[firstPlusIndex].start;
    const distToStart = distanceSq(refPoint, result[0]);
    const distToEnd = distanceSq(refPoint, result[result.length - 1]);
    if (distToEnd < distToStart) {
      result.reverse();
    }
  }

  return result;
}

/** Threshold for considering two endpoints as connected (~15 meters). */
const CONNECTIVITY_THRESHOLD = 0.00015;

/**
 * Checks whether two points are within the connectivity threshold.
 */
function arePointsConnected(a: ILatLng, b: ILatLng): boolean {
  return Math.abs(a.lat - b.lat) < CONNECTIVITY_THRESHOLD && Math.abs(a.lng - b.lng) < CONNECTIVITY_THRESHOLD;
}

/**
 * Stitches multiple decoded polylines into a single contiguous path.
 * Uses a graph-based approach: builds a connectivity graph between polylines,
 * finds connected components, and performs DFS traversal within each component
 * to produce a faithful spatial path (with backtracking for branching topologies).
 *
 * @param polylines - Array of decoded polylines (each already internally ordered)
 * @returns A single merged polyline in spatial order
 */
export function stitchPolylines(polylines: ILatLng[][]): ILatLng[] {
  if (polylines.length === 0) return [];
  if (polylines.length === 1) return [...polylines[0]];

  const adj = buildPolylineAdjacency(polylines);
  const ordered = orderPolylinesByDfs(polylines, adj);
  const { merged, didBacktrack } = mergeOrderedPolylines(polylines, ordered);

  // Normalize orientation for linear chains: ensure the polyline starts
  // from the endpoint with the smaller latitude (or longitude as tiebreaker).
  // Skip normalization if the path has backtracking (branching topology),
  // as reversing a backtracked path would break spatial coherence.
  if (!didBacktrack && merged.length >= 2) {
    const start = merged[0];
    const end = merged[merged.length - 1];
    const shouldReverse = start.lat > end.lat || (start.lat === end.lat && start.lng > end.lng);
    if (shouldReverse) {
      merged.reverse();
    }
  }

  return merged;
}

/**
 * Builds an adjacency list between polylines based on shared endpoints.
 */
function buildPolylineAdjacency(polylines: ILatLng[][]): number[][] {
  const adj: number[][] = polylines.map((): number[] => []);
  for (let i = 0; i < polylines.length; i++) {
    for (let j = i + 1; j < polylines.length; j++) {
      if (arePolylineEndpointsAdjacent(polylines[i], polylines[j])) {
        adj[i].push(j);
        adj[j].push(i);
      }
    }
  }
  return adj;
}

/**
 * Orders polylines using DFS traversal starting from a leaf node.
 * Appends any disconnected polylines at the end.
 */
function orderPolylinesByDfs(polylines: ILatLng[][], adj: number[][]): number[] {
  let startIdx = 0;
  for (let i = 0; i < polylines.length; i++) {
    if (adj[i].length <= 1) {
      startIdx = i;
      break;
    }
  }

  const ordered: number[] = [];
  const visited = new Set<number>();
  const stack: number[] = [startIdx];

  while (stack.length > 0) {
    const idx = stack.pop();
    if (visited.has(idx)) continue;
    visited.add(idx);
    ordered.push(idx);

    const neighbors = adj[idx];
    for (let i = neighbors.length - 1; i >= 0; i--) {
      if (!visited.has(neighbors[i])) {
        stack.push(neighbors[i]);
      }
    }
  }

  // Include any disconnected polylines at the end
  for (let i = 0; i < polylines.length; i++) {
    if (!visited.has(i)) {
      ordered.push(i);
    }
  }

  return ordered;
}

/**
 * Merges polylines in the given order into a single path.
 * Orients the first polyline toward the second, backtracks when needed
 * for branching topologies, and deduplicates shared boundary points.
 *
 * @returns The merged path and whether any backtracking occurred.
 */
function mergeOrderedPolylines(polylines: ILatLng[][], ordered: number[]): { merged: ILatLng[]; didBacktrack: boolean } {
  let didBacktrack = false;
  const merged = orientFirstPolyline(polylines, ordered);

  for (let i = 1; i < ordered.length; i++) {
    const nextPoly = polylines[ordered[i]];
    const mergedEnd = merged[merged.length - 1];

    const connectsToTail = arePointsConnected(mergedEnd, nextPoly[0]) || arePointsConnected(mergedEnd, nextPoly[nextPoly.length - 1]);

    if (!connectsToTail) {
      didBacktrack = true;
      const backtrack = buildPolylineBacktrack(merged, nextPoly);
      for (const point of backtrack) {
        merged.push(point);
      }
    }

    appendOrientedPolyline(merged, nextPoly);
  }

  return { merged, didBacktrack };
}

/**
 * Orients the first polyline so its connected endpoint faces the second polyline.
 */
function orientFirstPolyline(polylines: ILatLng[][], ordered: number[]): ILatLng[] {
  const firstPoly = polylines[ordered[0]];
  if (ordered.length <= 1) return [...firstPoly];

  const secondPoly = polylines[ordered[1]];
  const firstStart = firstPoly[0];
  const firstEnd = firstPoly[firstPoly.length - 1];
  const secStart = secondPoly[0];
  const secEnd = secondPoly[secondPoly.length - 1];

  const endConnects = arePointsConnected(firstEnd, secStart) || arePointsConnected(firstEnd, secEnd);
  const startConnects = arePointsConnected(firstStart, secStart) || arePointsConnected(firstStart, secEnd);

  return startConnects && !endConnects ? [...firstPoly].reverse() : [...firstPoly];
}

/**
 * Orients a polyline to connect to the merged tail and appends it,
 * deduplicating the shared boundary point if present.
 */
function appendOrientedPolyline(merged: ILatLng[], nextPoly: ILatLng[]): void {
  const lastPoint = merged[merged.length - 1];
  const dToStart = distanceSq(lastPoint, nextPoly[0]);
  const dToEnd = distanceSq(lastPoint, nextPoly[nextPoly.length - 1]);
  const oriented = dToEnd < dToStart ? [...nextPoly].reverse() : nextPoly;

  const startIndex = arePointsConnected(merged[merged.length - 1], oriented[0]) ? 1 : 0;
  for (let j = startIndex; j < oriented.length; j++) {
    merged.push(oriented[j]);
  }
}

/**
 * Checks if two polylines share an endpoint within the connectivity threshold.
 */
function arePolylineEndpointsAdjacent(polyA: ILatLng[], polyB: ILatLng[]): boolean {
  const aStart = polyA[0];
  const aEnd = polyA[polyA.length - 1];
  const bStart = polyB[0];
  const bEnd = polyB[polyB.length - 1];

  return (
    arePointsConnected(aEnd, bStart) ||
    arePointsConnected(aEnd, bEnd) ||
    arePointsConnected(aStart, bStart) ||
    arePointsConnected(aStart, bEnd)
  );
}

/**
 * Retraces the merged path backwards to the point closest to the next polyline's endpoints.
 */
function buildPolylineBacktrack(merged: ILatLng[], nextPoly: ILatLng[]): ILatLng[] {
  const nextStart = nextPoly[0];
  const nextEnd = nextPoly[nextPoly.length - 1];

  let bestIndex = merged.length - 1;
  let bestDist = Infinity;

  for (let idx = 0; idx < merged.length; idx++) {
    const dStart = distanceSq(merged[idx], nextStart);
    const dEnd = distanceSq(merged[idx], nextEnd);
    const d = Math.min(dStart, dEnd);
    if (d < bestDist) {
      bestDist = d;
      bestIndex = idx;
    }
  }

  const backtrack: ILatLng[] = [];
  for (let idx = merged.length - 2; idx >= bestIndex; idx--) {
    backtrack.push(merged[idx]);
  }

  return backtrack;
}
